import { useAuth } from "common";
import React, { useEffect, useState, useRef } from "react";
import { Button, Text, XStack, YStack, Separator, Spinner } from "tamagui";

import { useAuthContext } from "../App";
import { HubsOverview } from "../components/HubsOverview";
import { SensorDataTable } from "../components/SensorDataTable";
import { SensorEventCard } from "../components/SensorEventCard";
import { SensorsOverview } from "../components/SensorsOverview";
import { SensorUptimeChartConnector } from "../components/SensorUptimeChartConnector";
import { useAppSync } from "../contexts/AppSyncContext";

// Define enum for event type filter
type EventTypeFilter = "ALL" | "DATA" | "BATTERY";

// Define an interface for sensor value data
interface SensorValueRow {
  timestamp: number | string;
  eventType: string;
  [key: string]: any; // For dynamic value fields
}

export const Dashboard = () => {
  const { signOut, currentAuthenticatedUser } = useAuth();
  const { checkAuthState } = useAuthContext();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
  const [latestSensorEvent, setLatestSensorEvent] = useState<any>(null);
  const [eventTypeFilter, setEventTypeFilter] =
    useState<EventTypeFilter>("ALL");
  const [filteredEvents, setFilteredEvents] = useState<any[]>([]);
  const [valueColumns, setValueColumns] = useState<string[]>([]);
  const [valueRows, setValueRows] = useState<SensorValueRow[]>([]);
  const valueRowsRef = useRef<SensorValueRow[]>([]);

  // ...existing code...

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
      // Trigger auth state check which will cause navigation
      await checkAuthState();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const {
    hubs,
    hubsLoading,
    hubsError,
    selectedHub,
    sensors,
    sensorsLoading,
    sensorsError,
    sensorEvents,
    sensorEventsLoading,
    sensorEventsError,
    fetchHubs,
    fetchSensorsByHub,
    fetchSensorEventsBySensor,
    setSelectedSensorId: setContextSelectedSensorId,
    selectHubById,
  } = useAppSync();

  // Fetch user information when component mounts
  useEffect(() => {
    const getUserInfo = async () => {
      try {
        setLoading(true);
        const userInfo = await currentAuthenticatedUser();
        setUser(userInfo);
      } catch (error) {
        console.error("Error fetching user info:", error);
      } finally {
        setLoading(false);
      }
    };

    getUserInfo();
  }, [currentAuthenticatedUser]);

  // Handle hub selection
  const handleSelectHub = async (hubId: string) => {
    selectHubById(hubId);
    // Clear selected sensor when switching hubs
    setSelectedSensorId(null);
    setContextSelectedSensorId(null);
  };

  // Handle sensor selection and fetch events
  const handleSelectSensor = async (sensorId: string) => {
    setSelectedSensorId(sensorId);
    setContextSelectedSensorId(sensorId);
    try {
      await fetchSensorEventsBySensor(sensorId, 10); // Fetch more events to ensure we have both types
    } catch (error) {
      console.error("Error fetching sensor events:", error);
    }
  };

  // Filter events based on the selected event type filter
  useEffect(() => {
    if (!sensorEvents || sensorEvents.length === 0) {
      setFilteredEvents([]);
      setLatestSensorEvent(null);
      return;
    }

    // Filter events for the selected sensor
    let filtered = [...sensorEvents].filter((event) =>
      selectedSensorId ? event.sensorId === selectedSensorId : true,
    );

    // Apply event type filter
    if (eventTypeFilter === "DATA") {
      filtered = filtered.filter((event) => event.eventType === "DATA");
    } else if (eventTypeFilter === "BATTERY") {
      filtered = filtered.filter((event) => event.eventType === "BATTERY");
    }

    // Sort by timestamp descending
    filtered.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    setFilteredEvents(filtered);

    // Set the latest event after filtering
    if (filtered.length > 0) {
      setLatestSensorEvent(filtered[0]);

      // Update the spreadsheet data
      updateSpreadsheetData(filtered);
    } else {
      setLatestSensorEvent(null);
    }
  }, [sensorEvents, eventTypeFilter, selectedSensorId]);

  // Function to update the spreadsheet data
  const updateSpreadsheetData = (events: any[]) => {
    if (!events.length) return;

    // Get all unique column names from all events
    const allColumns = new Set<string>();
    const rows: SensorValueRow[] = [];

    // Process the events to extract columns and create rows
    events.forEach((event) => {
      const values = parseJsonSafely(event.values);
      if (values && typeof values === "object") {
        // Add all keys to our columns set
        Object.keys(values).forEach((key) => allColumns.add(key));

        // Create a row for this event
        const row: SensorValueRow = {
          timestamp: event.timestamp, // Store raw timestamp for sorting
          eventType: event.eventType,
          ...values, // Spread the values directly into the row
        };

        rows.push(row);
      }
    });

    // Sort rows by timestamp (descending)
    rows.sort((a, b) => {
      const timestampA = typeof a.timestamp === "number" ? a.timestamp : 0;
      const timestampB = typeof b.timestamp === "number" ? b.timestamp : 0;
      return timestampB - timestampA;
    });

    // Get a sorted array of all column names
    const sortedColumns = Array.from(allColumns).sort();

    // Update the state with the new columns and rows
    setValueColumns(sortedColumns);
    setValueRows(rows);
    valueRowsRef.current = rows;
  };

  // Parse JSON string to object safely or return the original value if it's already an object
  const parseJsonSafely = (value: any) => {
    if (!value) return {};

    // If it's already an object, return it
    if (typeof value === "object") return value;

    // If it's a string, try to parse it
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch (e) {
        console.error("Error parsing JSON:", e);
        return {};
      }
    }

    // For any other type, return an empty object
    return {};
  };

  // Format timestamp correctly (convert from seconds to milliseconds if needed)
  const formatTimestamp = (timestamp: number | null | undefined) => {
    if (!timestamp) return "Unknown";

    // Check if timestamp is in seconds (typical Unix timestamp) or milliseconds
    // If it's in seconds (less than year 2100), multiply by 1000 to convert to ms
    const timestampMs = timestamp < 4000000000 ? timestamp * 1000 : timestamp;

    return new Date(timestampMs).toLocaleString();
  };

  // Function to download the table data as CSV
  const downloadCSV = () => {
    if (valueRows.length === 0 || valueColumns.length === 0) {
      alert("No data available to download");
      return;
    }

    // Create CSV header row with 'timestamp' and 'eventType' first, followed by value columns
    const headers = ["timestamp", "eventType", ...valueColumns];
    const csvContent = [
      // Header row
      headers.join(","),
      // Data rows
      ...valueRows.map((row) =>
        headers
          .map((header) => {
            // Format timestamp for CSV
            if (header === "timestamp" && row.timestamp) {
              return formatTimestamp(row.timestamp);
            }
            // Handle missing values
            if (row[header] === undefined) return "";

            // Handle objects by converting to JSON string
            const value =
              typeof row[header] === "object"
                ? JSON.stringify(row[header])
                : String(row[header]);

            // Escape strings with commas or quotes
            return value.includes(",") || value.includes('"')
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          })
          .join(","),
      ),
    ].join("\n");

    // Create blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    link.setAttribute(
      "download",
      `sensor-data-${selectedSensorId}-${timestamp}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Set up auto-refresh when new events arrive
  useEffect(() => {
    if (!selectedSensorId) return;

    const intervalId = setInterval(() => {
      fetchSensorEventsBySensor(selectedSensorId, 10);
    }, 60000); // Refresh every minute

    return () => clearInterval(intervalId);
  }, [selectedSensorId, fetchSensorEventsBySensor]);

  return (
    <YStack flex={1} backgroundColor="$background">
      <XStack
        padding="$4"
        backgroundColor="$backgroundHover"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text fontSize="$10" fontWeight="bold">
          HomeGuardian Data Portal
        </Text>
        <Button onPress={handleLogout} backgroundColor="$red10">
          Logout
        </Button>
      </XStack>

      <YStack flex={1} padding="$2">
        <XStack space="$2" flex={1}>
          <YStack width={320} space="$2">
            <YStack
              backgroundColor="$backgroundHover"
              padding="$3"
              borderRadius="$4"
              space="$2"
            >
              <Text fontSize="$4" fontWeight="bold">
                Welcome, {user?.username || "User"}
              </Text>
              <Separator />
              {loading ? (
                <Text marginTop="$2">Loading user information...</Text>
              ) : user ? (
                <YStack marginTop="$2" space="$1">
                  {user.attributes && (
                    <>
                      <Text>Email: {user.attributes.email}</Text>
                      {user.attributes.name && (
                        <Text>Name: {user.attributes.name}</Text>
                      )}
                    </>
                  )}
                </YStack>
              ) : (
                <Text marginTop="$2" color="$red10">
                  User information not available
                </Text>
              )}
            </YStack>

            {/* Hubs Overview Card */}
            <HubsOverview
              hubs={hubs}
              hubsLoading={hubsLoading}
              hubsError={hubsError}
              selectedHub={selectedHub}
              fetchHubs={fetchHubs}
              handleSelectHub={handleSelectHub}
            />

            {/* Sensors Data Card */}
            <SensorsOverview
              selectedHub={selectedHub}
              sensors={sensors}
              sensorsLoading={sensorsLoading}
              sensorsError={sensorsError}
              selectedSensorId={selectedSensorId}
              fetchSensorsByHub={fetchSensorsByHub}
              handleSelectSensor={handleSelectSensor}
            />
          </YStack>
          <YStack flex={1} space="$2">
            {/* Sensor Events Card */}
            {selectedSensorId && (
              <YStack
                backgroundColor="$backgroundHover"
                padding="$3"
                borderRadius="$4"
                space="$2"
              >
                <XStack justifyContent="space-between" alignItems="center">
                  <XStack space="$2" alignItems="center" flex={1}>
                    <Text fontSize="$4" fontWeight="bold">
                      Sensor Events
                    </Text>
                    <XStack
                      backgroundColor="$backgroundStrong"
                      padding="$1"
                      borderRadius="$4"
                      alignItems="center"
                      marginLeft="$2"
                    >
                      <Button
                        size="$1"
                        theme={eventTypeFilter === "ALL" ? "active" : "gray"}
                        onPress={() => setEventTypeFilter("ALL")}
                      >
                        All
                      </Button>
                      <Button
                        size="$1"
                        theme={eventTypeFilter === "DATA" ? "active" : "gray"}
                        onPress={() => setEventTypeFilter("DATA")}
                      >
                        DATA
                      </Button>
                      <Button
                        size="$1"
                        theme={
                          eventTypeFilter === "BATTERY" ? "active" : "gray"
                        }
                        onPress={() => setEventTypeFilter("BATTERY")}
                      >
                        BATTERY
                      </Button>
                    </XStack>
                    <Text fontSize="$1" opacity={0.7}>
                      {filteredEvents.length > 0
                        ? `${filteredEvents.length} found`
                        : "No events"}
                    </Text>
                  </XStack>
                  <Button
                    onPress={() =>
                      fetchSensorEventsBySensor(selectedSensorId, 10)
                    }
                    size="$2"
                    theme="blue"
                    icon={<Text>↻</Text>}
                  >
                    Refresh
                  </Button>
                </XStack>

                <Separator marginTop="$2" />

                {sensorEventsLoading ? (
                  <YStack alignItems="center" padding="$4">
                    <Spinner size="large" />
                    <Text marginTop="$2">Loading sensor events...</Text>
                  </YStack>
                ) : sensorEventsError ? (
                  <Text color="$red10">
                    Error loading events: {sensorEventsError.message}
                  </Text>
                ) : !latestSensorEvent ? (
                  <Text padding="$2">
                    {eventTypeFilter === "ALL"
                      ? "No events found for this sensor."
                      : `No ${eventTypeFilter} events found for this sensor.`}
                  </Text>
                ) : (
                  <SensorEventCard
                    latestSensorEvent={latestSensorEvent}
                    formatTimestamp={formatTimestamp}
                    parseJsonSafely={parseJsonSafely}
                  />
                )}
              </YStack>
            )}

            {/* Spreadsheet-like Sensor Data View */}
            {selectedSensorId && (
              <>
                {/* Wrap component in an error boundary to catch any rendering errors */}
                {(() => {
                  try {
                    return (
                      <SensorUptimeChartConnector
                        sensorId={selectedSensorId}
                        maxWidth={1800}
                      />
                    );
                  } catch (err) {
                    console.error(
                      "Error rendering SensorUptimeChartConnector:",
                      err,
                    );
                    return (
                      <YStack
                        padding="$4"
                        backgroundColor="$backgroundHover"
                        borderRadius="$4"
                      >
                        <Text color="$red10">
                          Failed to load sensor data chart: {err?.toString()}
                        </Text>
                      </YStack>
                    );
                  }
                })()}
                <SensorDataTable
                  valueRows={valueRows}
                  valueColumns={valueColumns}
                  formatTimestamp={formatTimestamp}
                  downloadCSV={downloadCSV}
                />
              </>
            )}
          </YStack>
        </XStack>
      </YStack>
    </YStack>
  );
};
